import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * One-tap add-ons inside the cart drawer. Forms post natively to /cart/add without
 * JavaScript; with it, the add happens in place and the drawer section morphs.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [error] - Shared error message for the rail.
 *
 * @extends {Component<Refs>}
 */
class CartUpsellComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Adds the submitted variant without leaving the drawer.
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    button?.setAttribute('aria-busy', 'true');
    this.refs.error?.classList.add('hidden');

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
        signal: this.#abortController.signal,
      });
      const data = await response.json();

      if (data.status) {
        this.#showError(data.description || data.message);
        return;
      }

      // The cart icon treats non-product-form counts as the new cart total.
      const firstSection = data.sections ? Object.values(data.sections)[0] : null;
      const countText = firstSection
        ? new DOMParser().parseFromString(firstSection, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent
        : null;

      this.dispatchEvent(
        new CartAddEvent({}, String(formData.get('id')), {
          source: 'cart-upsell-component',
          itemCount: countText ? Number.parseInt(countText, 10) : undefined,
          variantId: String(formData.get('id')),
          sections: data.sections,
        })
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // Fall back to a normal form post so the shopper still gets the product.
      form.submit();
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };

  /** @param {string} message */
  #showError(message) {
    const { error } = this.refs;
    if (!error) return;
    error.textContent = message;
    error.classList.remove('hidden');
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
